import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import TableContent from '../../../components/TableContent'
import { apiRequest } from '../../../helpers/apiRequest'
import { AuthorizationContext } from '../../../components/auth/AuthProvider'
import { Tooltip, notification, Modal, Upload, Select, Dropdown, Button } from 'antd'
import { useLanguage } from '../../../components/context/LanguageContext'
import { FaSpinner } from 'react-icons/fa'
import { FaRegPlayCircle } from 'react-icons/fa'
import { FiUpload } from 'react-icons/fi'
import { DownOutlined } from '@ant-design/icons'
import EmailMessageDetails from './EmailMessageDetails'

const messagesUrl = 'emails'

const EmailAgentMessages = () => {
  const tableRef = useRef(null)
  const { userData } = useContext(AuthorizationContext)
  const languageContext = useLanguage()
  const [processingState, setProcessingState] = useState({})
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [fileList, setFileList] = useState([])
  const [selectedAgentId, setSelectedAgentId] = useState(null)
  const [importing, setImporting] = useState(false)
  const [agents, setAgents] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [bulkProcessing, setBulkProcessing] = useState(false)

  // Load agents on mount
  React.useEffect(() => {
    const loadAgents = async () => {
      const response = await apiRequest(`${process.env.REACT_APP_API}agents/emails`, 'GET', {})
      if (response?.ok && response?.data?.data) {
        setAgents(response.data.data)
        if (response.data.data.length > 0) {
          setSelectedAgentId(response.data.data[0].id)
        }
      }
    }
    loadAgents()
  }, [])

  const canProcess = useMemo(
    () => userData?.permissions?.includes('agent-email-message-process'),
    [userData?.permissions],
  )

  const canImport = useMemo(
    () => userData?.permissions?.includes('agent-email-message-create'),
    [userData?.permissions],
  )

  const canDelete = useMemo(
    () => userData?.permissions?.includes('agent-email-message-delete'),
    [userData?.permissions],
  )

  const handleBulkProcess = useCallback(async () => {
    if (!canProcess || selectedRows.length === 0 || bulkProcessing) {
      return
    }

    setBulkProcessing(true)

    let successCount = 0
    let errorCount = 0

    for (const record of selectedRows) {
      const endpoint = `${process.env.REACT_APP_API}${messagesUrl}/${record.id}/process`
      const response = await apiRequest(endpoint, 'POST', {})

      if (response?.ok) {
        successCount++
        const updatedMessage = response?.data?.message
        if (updatedMessage && tableRef.current?.updateRecord) {
          tableRef.current.updateRecord(updatedMessage.id, {
            status: updatedMessage.status,
            response: updatedMessage.response,
            processedAt: updatedMessage.processedAt,
            processingLog: updatedMessage.processingLog,
            error: updatedMessage.error,
          })
        }
      } else {
        errorCount++
      }
    }

    if (successCount > 0) {
      notification.success({
        message: languageContext.t('Success'),
        description: `${successCount} ${languageContext.t('emails processed successfully')}`,
      })
    }

    if (errorCount > 0) {
      notification.error({
        message: languageContext.t('Error'),
        description: `${errorCount} ${languageContext.t('emails failed to process')}`,
      })
    }

    setSelectedRows([])
    setBulkProcessing(false)
  }, [canProcess, selectedRows, bulkProcessing, languageContext])

  const handleBulkSendToERP = useCallback(async () => {
    if (!canProcess || selectedRows.length === 0 || bulkProcessing) {
      return
    }

    setBulkProcessing(true)

    let successCount = 0
    let errorCount = 0

    for (const record of selectedRows) {
      const endpoint = `${process.env.REACT_APP_API}${messagesUrl}/${record.id}/sendtoerp`
      const response = await apiRequest(endpoint, 'POST', {})

      if (response?.ok) {
        successCount++
        const updatedMessage = response?.data?.message
        if (updatedMessage && tableRef.current?.updateRecord) {
          tableRef.current.updateRecord(updatedMessage.id, {
            status: updatedMessage.status,
            erpResponse: updatedMessage.erpResponse,
            sentAt: updatedMessage.sentAt,
            error: updatedMessage.error,
          })
        }
      } else {
        errorCount++
      }
    }

    if (successCount > 0) {
      notification.success({
        message: languageContext.t('Success'),
        description: `${successCount} ${languageContext.t('emails sent to ERP successfully')}`,
      })
    }

    if (errorCount > 0) {
      notification.error({
        message: languageContext.t('Error'),
        description: `${errorCount} ${languageContext.t('emails failed to send to ERP')}`,
      })
    }

    setSelectedRows([])
    setBulkProcessing(false)
  }, [canProcess, selectedRows, bulkProcessing, languageContext])

  const handleBulkDelete = useCallback(async () => {
    if (!canDelete || selectedRows.length === 0 || bulkProcessing) {
      return
    }

    Modal.confirm({
      title: languageContext.t('Confirm Delete'),
      content: `${languageContext.t('Are you sure you want to delete')} ${selectedRows.length} ${languageContext.t('emails')}?`,
      okText: languageContext.t('Delete'),
      okType: 'danger',
      cancelText: languageContext.t('Cancel'),
      onOk: async () => {
        setBulkProcessing(true)

        let successCount = 0
        let errorCount = 0

        for (const record of selectedRows) {
          const endpoint = `${process.env.REACT_APP_API}${messagesUrl}/${record.id}`
          const response = await apiRequest(endpoint, 'DELETE', {})

          if (response?.ok) {
            successCount++
          } else {
            errorCount++
          }
        }

        if (successCount > 0) {
          notification.success({
            message: languageContext.t('Success'),
            description: `${successCount} ${languageContext.t('emails deleted successfully')}`,
          })
          // Refresh table
          if (tableRef.current?.refreshTable) {
            tableRef.current.refreshTable()
          }
        }

        if (errorCount > 0) {
          notification.error({
            message: languageContext.t('Error'),
            description: `${errorCount} ${languageContext.t('emails failed to delete')}`,
          })
        }

        setSelectedRows([])
        setBulkProcessing(false)
      },
    })
  }, [canDelete, selectedRows, bulkProcessing, languageContext])

  const handleDeleteAll = useCallback(async () => {
    if (!canDelete) return
    Modal.confirm({
      title: languageContext.t('Sterge toate emailurile'),
      content: languageContext.t('Esti sigur ca vrei sa stergi TOATE emailurile din Inbox? Aceasta actiune nu poate fi anulata.'),
      okText: languageContext.t('Sterge toate'),
      okButtonProps: { danger: true },
      cancelText: languageContext.t('Anuleaza'),
      onOk: async () => {
        setBulkProcessing(true)
        try {
          await apiRequest(`${process.env.REACT_APP_API}${messagesUrl}/delete-all`, 'DELETE', {})
          notification.success({ message: languageContext.t('Toate emailurile au fost sterse.') })
          if (tableRef.current?.refreshTable) tableRef.current.refreshTable()
          setSelectedRows([])
          setSelectedMessage(null)
        } catch (e) {
          notification.error({ message: languageContext.t('Eroare la stergere.') })
        } finally {
          setBulkProcessing(false)
        }
      },
    })
  }, [canDelete, bulkProcessing, languageContext])

  const handleRowSelection = useCallback((rows) => {
    setSelectedRows(rows || [])
  }, [])

  const handleImport = useCallback(() => {
    setShowImportModal(true)
  }, [])

  const handleImportSubmit = useCallback(async () => {
    if (!fileList || fileList.length === 0) {
      notification.error({
        message: languageContext.t('Error'),
        description: languageContext.t('Please select at least one .msg or .eml file'),
      })
      return
    }

    if (!selectedAgentId) {
      notification.error({
        message: languageContext.t('Error'),
        description: languageContext.t('Please select an agent'),
      })
      return
    }

    setImporting(true)

    try {
      const formData = new FormData()
      fileList.forEach((file) => {
        formData.append('files', file.originFileObj || file)
      })

      const endpoint = `${process.env.REACT_APP_API}${messagesUrl}/import/${selectedAgentId}`
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      const result = await response.json()

      if (response.ok) {
        notification.success({
          message: languageContext.t('Success'),
          description: result.message || `Imported ${result.data?.imported || 0} emails`,
        })
        setShowImportModal(false)
        setFileList([])
        // Refresh table
        if (tableRef.current?.refreshTable) {
          tableRef.current.refreshTable()
        }
      } else {
        notification.error({
          message: languageContext.t('Error'),
          description: result.message || 'Failed to import emails',
        })
      }
    } catch (error) {
      notification.error({
        message: languageContext.t('Error'),
        description: error.message || 'Failed to import emails',
      })
    } finally {
      setImporting(false)
    }
  }, [fileList, selectedAgentId, languageContext])

  const handleCancelImport = useCallback(() => {
    setShowImportModal(false)
    setFileList([])
  }, [])

  const handleProcess = useCallback(async (record) => {
    if (!canProcess || !record?.id) {
      return
    }

    setProcessingState((prev) => ({ ...prev, [record.id]: true }))

    const endpoint = `${process.env.REACT_APP_API}${messagesUrl}/${record.id}/process`
    const response = await apiRequest(endpoint, 'POST', {})

    if (response?.ok) {
      const updatedMessage = response?.data?.message

      if (updatedMessage && tableRef.current?.updateRecord) {
        tableRef.current.updateRecord(updatedMessage.id, {
          status: updatedMessage.status,
          response: updatedMessage.response,
          processedAt: updatedMessage.processedAt,
          processingLog: updatedMessage.processingLog,
          error: updatedMessage.error,
        })
        setSelectedMessage((prev) =>
          prev && prev.id === updatedMessage.id ? { ...prev, ...updatedMessage } : prev,
        )
      }

      notification.success({
        message: languageContext.t('Success'),
        description: languageContext.t('Email processed successfully'),
      })
    }

    setProcessingState((prev) => {
      const next = { ...prev }
      delete next[record.id]
      return next
    })
  }, [canProcess, languageContext])

  const extensions = useMemo(
    () => ({
      renderAdditionalExtensions: () => null,
    }),
    [],
  )

  const handleRowSelect = useCallback((record) => {
    setSelectedMessage(record || null)
  }, [])

  const uploadProps = {
    multiple: true,
    accept: '.msg,.eml',
    fileList: fileList,
    beforeUpload: (file) => {
      const filename = file.name.toLowerCase()
      const isValidFile = filename.endsWith('.msg') || filename.endsWith('.eml')
      if (!isValidFile) {
        notification.error({
          message: languageContext.t('Error'),
          description: languageContext.t('You can only upload .msg and .eml files'),
        })
        return Upload.LIST_IGNORE
      }
      setFileList((prev) => [...prev, file])
      return false
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid))
    },
  }

  const extraHeaderButtons = useMemo(
    () => (
      <>
        {selectedRows.length > 0 && (canProcess || canDelete) && (
          <Dropdown
            menu={{
              items: [
                canProcess && {
                  key: 'process',
                  label: languageContext.t('Process'),
                  onClick: handleBulkProcess,
                  disabled: bulkProcessing,
                },
                canProcess && {
                  key: 'sendtoerp',
                  label: languageContext.t('Send to ERP'),
                  onClick: handleBulkSendToERP,
                  disabled: bulkProcessing,
                },
                canDelete && {
                  key: 'delete',
                  label: languageContext.t('Delete'),
                  onClick: handleBulkDelete,
                  disabled: bulkProcessing,
                  danger: true,
                },
              ].filter(Boolean),
            }}
            disabled={bulkProcessing}
          >
            <Button style={{ marginLeft: '8px' }} loading={bulkProcessing}>
              {languageContext.t('Actiuni')} <DownOutlined />
            </Button>
          </Dropdown>
        )}
        {canDelete && (
          <Tooltip title={languageContext.t('Sterge toate emailurile')}>
            <Button
              danger
              style={{ marginLeft: '8px' }}
              onClick={handleDeleteAll}
              loading={bulkProcessing}
            >
              {languageContext.t('Sterge toate')}
            </Button>
          </Tooltip>
        )}
      </>
    ),
    [canImport, canProcess, canDelete, selectedRows, bulkProcessing, handleImport, handleBulkProcess, handleBulkSendToERP, handleBulkDelete, handleDeleteAll, languageContext],
  )

  return (
    <>
      <TableContent
        ref={tableRef}
        url={messagesUrl}
        type="agent-email-message"
        item="Email Message"
        items="Email Messages"
        rowHasCheckbox={true}
        extensions={extensions}
        onRowSelect={handleRowSelect}
        onRowsSelectionChange={handleRowSelection}
        extraHeaderButtons={extraHeaderButtons}
        hideRefresh={true}
      />
      {selectedMessage && <EmailMessageDetails message={selectedMessage} />}
      
      <Modal
        title={languageContext.t('Import MSG/EML Files')}
        open={showImportModal}
        onOk={handleImportSubmit}
        onCancel={handleCancelImport}
        confirmLoading={importing}
        okText={languageContext.t('Import')}
        cancelText={languageContext.t('Cancel')}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px' }}>
            {languageContext.t('Select Agent')}:
          </label>
          <Select
            style={{ width: '100%' }}
            value={selectedAgentId}
            onChange={setSelectedAgentId}
            placeholder={languageContext.t('Select an agent')}
          >
            {agents.map((agent) => (
              <Select.Option key={agent.id} value={agent.id}>
                {agent.name}
              </Select.Option>
            ))}
          </Select>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>
            {languageContext.t('Select MSG/EML Files')}:
          </label>
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <FiUpload size={48} />
            </p>
            <p className="ant-upload-text">
              {languageContext.t('Click or drag MSG/EML files to this area to upload')}
            </p>
            <p className="ant-upload-hint">
              {languageContext.t('Support for multiple file selection')}
            </p>
          </Upload.Dragger>
        </div>
      </Modal>
    </>
  )
}

export default EmailAgentMessages
